import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useGetAdvisorsQuery, useTenantDetails } from '@circuitry-ai/doc-data';
import type { Advisor } from '@circuitry-ai/doc-data';
import {
  applyPalChatbotHostMode,
  getCachedDefaultAdvisorPalMode,
  getPalSessionMode,
  resolveAdvisorEmbedModeFromRecord,
  resolveDefaultAdvisor,
  resolvePalModeForTenant,
  shouldForcePalDockMode,
  type PalChatbotMode,
} from '../utils/commonutils';

/**
 * Warm the advisors list on every authenticated page (same RTK cache as Dashboard)
 * and stamp default-advisor palview onto PAL session/tag as soon as it is known.
 * Open PAL stays instant — it never waits on this request.
 */
export function useSyncDefaultAdvisorPalMode(): PalChatbotMode {
  const token = useSelector((state: RootState) => state.auth.token);
  const { activeChatAdvisor, allAdvisors } = useSelector((state: RootState) => state.advisor);
  const { data: advisorsResponse } = useGetAdvisorsQuery(undefined, { skip: !token });
  const { tenantDetails } = useTenantDetails({ enabled: Boolean(token) });

  const advisors: Advisor[] = advisorsResponse?.data ?? allAdvisors ?? [];
  const defaultAdvisor = resolveDefaultAdvisor(advisors, activeChatAdvisor);
  const fromApi = defaultAdvisor
    ? resolveAdvisorEmbedModeFromRecord(defaultAdvisor)
    : null;
  const resolvedMode = resolvePalModeForTenant(
    tenantDetails?.org_name,
    fromApi ?? getPalSessionMode()
  );

  useEffect(() => {
    if (shouldForcePalDockMode(tenantDetails?.org_name)) {
      applyPalChatbotHostMode('dock');
      return;
    }
    if (fromApi) {
      applyPalChatbotHostMode(fromApi);
      return;
    }
    const cached = getCachedDefaultAdvisorPalMode();
    if (cached) {
      applyPalChatbotHostMode(cached);
    }
  }, [fromApi, tenantDetails?.org_name]);

  return resolvedMode;
}
